/**
 * useSchedulingProfessionals
 * Feature: 005-clinic-scheduling
 *
 * Returns active organization members that can be used in scheduling.
 *
 * sessions.professional_id now resolves directly to profiles.id, with
 * organization membership enforced at the database level.
 */

import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/features/auth'

export interface SchedulingProfessionalOption {
  id: string
  full_name: string
}

async function fetchSchedulingProfessionals(
  organizationId: string
): Promise<SchedulingProfessionalOption[]> {
  const { data, error } = await supabase
    .from('organization_members')
    .select(`
      profile_id,
      is_active,
      profiles!inner (
        id,
        full_name
      )
    `)
    .eq('organization_id', organizationId)
    .in('role', ['admin', 'professional'])
    .eq('is_active', true)

  if (error) throw error

  return (data ?? []).map((member) => {
    const profile = Array.isArray(member.profiles)
      ? member.profiles[0]
      : member.profiles
    return {
      id: member.profile_id,
      full_name: (profile as { id: string; full_name: string } | null)?.full_name ?? 'Profissional',
    }
  })
}

export function useSchedulingProfessionals() {
  const { currentOrganization } = useAuth()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: ['scheduling-professionals', organizationId],
    queryFn: () => fetchSchedulingProfessionals(organizationId!),
    enabled: !!organizationId,
  })
}
